import React, { FC, useMemo, useState, useEffect } from "react";
import { Table, notification } from "antd";
import { useTranslation } from "react-i18next";
import { getQueryParam, updateQueryParam } from "shared/lib";
import {
  useGetAllBatchesQuery,
  useDeleteBatchMutation,
  BatchItem,
  useGetAllReportsQuery,
  useExecuteReportMutation,
  ExecuteReport,
  useGetAllOrdersWithNoParamsQuery,
  useGetAllCompaniesQuery,
  ReportType,
  useCreateFortnoxInvoiceMutation,
  CreateFortnoxInvoiceResponse,
} from "shared/api";
import { adaptOrders } from "shared/adapters";
import {
  useConfirmDeleteModal,
  usePagination,
  useDebouncedTableFilter,
  useGetBatchesTableColumns,
} from "shared/hooks";
import { handleRequestError } from "shared/helpers";
import { RenderClearFiltersButton, Loader } from "shared/ui";
import {
  formatOrdersForDisplay,
  extractOrderIdsForReport,
  extractPackagingIds,
} from "../utils/batchHelpers";
import { FortnoxInvoiceResultModal } from "./FortnoxInvoiceResultModal";
import { PdfReportModal } from "./PdfReportModal";

interface BatchesTableProps {
  showEditModal: (batch: BatchItem) => void;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  isFortnoxLoggedIn?: boolean;
}

const BatchesTable: FC<BatchesTableProps> = ({
  showEditModal,
  hasDeletePermission,
  hasEditPermission,
  isFortnoxLoggedIn,
}) => {
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [fortnoxInvoiceResult, setFortnoxInvoiceResult] =
    useState<CreateFortnoxInvoiceResponse | null>(null);
  const [showFortnoxModal, setShowFortnoxModal] = useState(false);

  const pagination = usePagination(20);
  const { offset, limit, pageSize, currentPage, setCurrentPage, setPageSize } =
    pagination;

  const {
    debouncedValue: debouncedBatchCodeValue,
    handleFilterChange: handleBatchCodeFilterChange,
    value: batchCodeFilterValue,
    clearFilterValue: clearBatchCodeFilter,
  } = useDebouncedTableFilter("BatchCode", 400);

  const {
    debouncedValue: debouncedNameValue,
    handleFilterChange: handleNameFilterChange,
    value: nameFilterValue,
    clearFilterValue: clearNameFilter,
  } = useDebouncedTableFilter("BatchName", 400);

  const {
    debouncedValue: debouncedDescriptionValue,
    handleFilterChange: handleDescriptionFilterChange,
    value: descriptionFilterValue,
    clearFilterValue: clearDescriptionFilter,
  } = useDebouncedTableFilter("BatchDescription", 400);

  const {
    debouncedValue: debouncedPackagingsValue,
    handleFilterChange: handlePackagingsFilterChange,
    value: packagingsFilterValue,
    clearFilterValue: clearPackagingsFilter,
  } = useDebouncedTableFilter("packagings", 400);

  const companyParam = getQueryParam("company");
  const [selectedCompanies, setSelectedCompanies] = useState<number[]>(
    companyParam ? companyParam.split(",").map(Number) : []
  );

  const ordersParam = getQueryParam("orders");
  const [selectedOrders, setSelectedOrders] = useState<number[]>(
    ordersParam ? ordersParam.split(",").map(Number) : []
  );

  const [shouldLoadOrders, setShouldLoadOrders] = useState(!!ordersParam);

  const {
    data: allOrders,
    isLoading: isLoadingOrders,
    isFetching: isFetchingOrders,
  } = useGetAllOrdersWithNoParamsQuery({}, { skip: !shouldLoadOrders });

  const {
    data: allCompanies,
    isLoading: isLoadingCompanies,
    isFetching: isFetchingCompanies,
  } = useGetAllCompaniesQuery({});

  const adaptedOrders = useMemo(
    () => adaptOrders(allOrders || []),
    [allOrders]
  );

  const adaptedCompanies = useMemo(
    () => allCompanies?.map((c) => ({ label: c.name, value: c.id })) || [],
    [allCompanies]
  );

  const handleOrdersDropdownOpen = () => {
    if (!shouldLoadOrders) {
      setShouldLoadOrders(true);
    }
  };

  const selectedOrderCodes = useMemo(() => {
    if (!allOrders || selectedOrders.length === 0) return [];
    return allOrders
      .filter((order) => selectedOrders.includes(order.id))
      .map((order) => order.order_code)
      .filter((code): code is string => !!code);
  }, [allOrders, selectedOrders]);

  const handleOrdersFilterChange = (values: number[]) => {
    setSelectedOrders(values);
    updateQueryParam("orders", values.length > 0 ? values.join(",") : "");
  };

  const clearOrdersFilter = () => {
    setSelectedOrders([]);
    updateQueryParam("orders", "");
  };

  const handleCompaniesFilterChange = (values: number[]) => {
    setSelectedCompanies(values);
    updateQueryParam("company", values.length > 0 ? values.join(",") : "");
  };

  const clearCompaniesFilter = () => {
    setSelectedCompanies([]);
    updateQueryParam("company", "");
  };

  const clearAllFilters = () => {
    clearNameFilter();
    clearDescriptionFilter();
    clearOrdersFilter();
    clearPackagingsFilter();
    clearCompaniesFilter();
    clearBatchCodeFilter();
  };

  const isActiveFiltering =
    nameFilterValue.length > 0 ||
    descriptionFilterValue.length > 0 ||
    selectedOrders.length > 0 ||
    packagingsFilterValue.length > 0 ||
    selectedCompanies.length > 0 ||
    batchCodeFilterValue.length > 0;

  const {
    data: batches,
    isLoading,
    isFetching,
  } = useGetAllBatchesQuery({
    offset,
    limit,
    expand:
      "orders,company,packagings.packaging.order,packagings.packaging.material",
    batch_code__icontains: debouncedBatchCodeValue,
    name__icontains: debouncedNameValue || undefined,
    description__icontains: debouncedDescriptionValue || undefined,
    company__in:
      selectedCompanies.length > 0 ? selectedCompanies.join(",") : undefined,
    orders__order__order_code__in:
      selectedOrderCodes.length > 0 ? selectedOrderCodes : undefined,
    packagings__packaging__name: debouncedPackagingsValue || undefined,
  });

  const [deleteBatch] = useDeleteBatchMutation();
  const [executeReport, { isLoading: isGeneratingPdf }] =
    useExecuteReportMutation();
  const [createFortnoxInvoice, { isLoading: isCreatingInvoice }] =
    useCreateFortnoxInvoiceMutation();

  const { data: reportsData, isLoading: isLoadingReports } =
    useGetAllReportsQuery({});

  const loadingListReport = useMemo(() => {
    if (!reportsData || !Array.isArray(reportsData)) return undefined;
    return reportsData.find(
      (report) => report.report_type === ReportType.MULTIPLE_COMPANY_PACKAGING
    );
  }, [reportsData]);

  const handleGenerateReport = async (batch: BatchItem) => {
    if (isLoadingReports || !loadingListReport) {
      if (!loadingListReport) {
        api.error({
          message: t("error"),
          description: "Report template not found",
        });
      }
      return;
    }

    const orderIds = extractOrderIdsForReport(batch);
    const packagingsIds = extractPackagingIds(batch.packagings);

    if (orderIds.length === 0) {
      api.error({
        message: t("error"),
        description: "No orders found in batch",
      });
      return;
    }

    try {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }

      const reportParams: ExecuteReport = {
        id: loadingListReport.id,
        material_item_id: null,
        packaging_id: null,
        order_id: null,
        material_id: null,
        order_production_datetime_start: null,
        order_production_datetime_end: null,
        report_format: "pdf",
        quality_result_id: null,
        packaging_ids: packagingsIds,
        drigging_rigg_field: null,
        drigging_rigg_datetime_start: null,
        drigging_rigg_datetime_end: null,
        drigging_rigg_n_points: null,
        company_id:
          typeof batch.company === "object" ? batch.company.id : batch.company,
        material_category_id_list: [],
        order_ids: orderIds,
        dynamic_tracker_fields: [],
        material_category_id: null,
        product_card_id: null,
        quality_result_material_item_timestamp_end: null,
        quality_result_material_item_timestamp_start: null,
      };

      const blob = await executeReport(reportParams).unwrap();
      const fileUrl = window.URL.createObjectURL(blob);
      setPdfUrl(fileUrl);
      api.success({
        message: t("success"),
        description: "Report generated successfully",
      });
    } catch (error) {
      api.error({
        message: t("error"),
        description: handleRequestError(error, "Failed to generate report"),
      });
    }
  };

  const deleteBatchHandler = async (batch: BatchItem) => {
    try {
      await deleteBatch(batch.id).unwrap();
      api.success({
        message: t("success"),
        description: "Batch successfully deleted",
      });
    } catch (error) {
      api.error({
        message: t("error"),
        description: handleRequestError(error, "Failed to delete batch"),
      });
    }
  };

  const showDeleteModal = useConfirmDeleteModal(
    "Are you sure you want to delete this batch?",
    deleteBatchHandler
  );

  const hasOrders = (batch: BatchItem): boolean => {
    return extractOrderIdsForReport(batch).length > 0;
  };

  const handleCreateFortnoxInvoice = async (batch: BatchItem) => {
    try {
      const result = await createFortnoxInvoice(batch.id).unwrap();
      setFortnoxInvoiceResult(result);
      setShowFortnoxModal(true);
      if (result.ok) {
        api.success({
          message: t("success"),
          description: "Fortnox invoice created successfully",
        });
      } else {
        api.warning({
          message: t("warning"),
          description: "Failed to create Fortnox invoice",
        });
      }
    } catch (error) {
      api.error({
        message: t("error"),
        description: handleRequestError(
          error,
          "Failed to create Fortnox invoice"
        ),
      });
    }
  };

  const columns = useGetBatchesTableColumns({
    t,
    showEditModal,
    hasEditPermission,
    hasDeletePermission,
    nameFilterValue,
    handleNameFilterChange,
    clearNameFilter,

    batchCodeFilterValue,
    handleBatchCodeFilterChange,
    clearBatchCodeFilter,

    descriptionFilterValue,
    handleDescriptionFilterChange,
    clearDescriptionFilter,
    selectedCompanies,
    adaptedCompanies,
    handleCompaniesFilterChange,
    clearCompaniesFilter,
    isLoadingCompanies: isLoadingCompanies || isFetchingCompanies,
    formatOrdersForDisplay,
    handleGenerateReport,
    showDeleteModal,
    selectedOrders,
    adaptedOrders,
    handleOrdersFilterChange,
    clearOrdersFilter,
    isLoadingOrders: isLoadingOrders || isFetchingOrders,
    onOrdersDropdownOpen: handleOrdersDropdownOpen,
    hasOrders,
    packagingsFilterValue,
    handlePackagingsFilterChange,
    clearPackagingsFilter,
    isFortnoxLoggedIn,
    handleCreateFortnoxInvoice,
  });

  useEffect(() => {
    return () => {
      if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  return (
    <>
      {contextHolder}
      <FortnoxInvoiceResultModal
        visible={showFortnoxModal}
        onClose={() => setShowFortnoxModal(false)}
        result={fortnoxInvoiceResult}
      />
      <PdfReportModal
        visible={!!pdfUrl && !isGeneratingPdf}
        pdfUrl={pdfUrl}
        onClose={() => {
          if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
          setPdfUrl(null);
        }}
      />
      {isActiveFiltering && (
        <RenderClearFiltersButton onClick={clearAllFilters} />
      )}
      <Table
        loading={isLoading || isFetching}
        columns={columns}
        dataSource={batches?.results}
        rowKey={(record) => record.id}
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage,
          onShowSizeChange(page, pageSize) {
            setPageSize(pageSize);
            setCurrentPage(page);
          },
          onChange(page) {
            setCurrentPage(page);
          },
          total: batches?.count,
        }}
      />
      {isCreatingInvoice ||
        (isGeneratingPdf && <Loader type="fullscreen-backdrop" />)}
    </>
  );
};

export { BatchesTable };
